import React, { useEffect, useMemo, useState } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { motion } from "framer-motion";
import { Search, X, Library as LibraryIcon, Sparkles, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LibraryCardImage } from "@/components/LibraryCardImage";

const TAROT_FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "Major", label: "Major Arcana", match: (c) => c.arcana === "Major" },
  { id: "Wands", label: "Wands", match: (c) => c.suit === "Wands" },
  { id: "Cups", label: "Cups", match: (c) => c.suit === "Cups" },
  { id: "Swords", label: "Swords", match: (c) => c.suit === "Swords" },
  { id: "Pentacles", label: "Pentacles", match: (c) => c.suit === "Pentacles" },
];

const ORACLE_FILTERS = [
  { id: "all", label: "All", match: () => true },
  { id: "Earth", label: "Earth", match: (c) => c.element === "Earth" },
  { id: "Air", label: "Air", match: (c) => c.element === "Air" },
  { id: "Fire", label: "Fire", match: (c) => c.element === "Fire" },
  { id: "Water", label: "Water", match: (c) => c.element === "Water" },
];

const ELEMENT_TINT = {
  Earth: "#7BA05B",
  Air: "#8FB8DE",
  Fire: "#D98A5B",
  Water: "#6C8CC7",
};

export default function Library() {
  usePageMeta({ title: "Tarot & Oracle Card Meanings Library - The Cardlight", description: "Browse every card in the Whispering Path Tarot (78 cards, upright and reversed meanings) and the 44-card Whispering Path Oracle.", path: "/library" });
  const { user, login } = useAuth();
  const [tarot, setTarot] = useState([]);
  const [oracle, setOracle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deck, setDeck] = useState("tarot"); // tarot | oracle
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [t, o] = await Promise.all([
          api.get("/decks/rider-waite/cards"),
          api.get("/decks/moon-nature/cards"),
        ]);
        if (!active) return;
        setTarot(t.data || []);
        setOracle(o.data || []);
      } catch (e) {
        // Leave arrays empty; UI shows an empty state.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setFavorites(new Set());
      if (filter === "fav") setFilter("all");
      return;
    }
    api
      .get("/favorites")
      .then((res) => setFavorites(new Set((res.data || []).map((f) => f.card_id))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleFavorite = async (card, e) => {
    if (e) e.stopPropagation();
    if (!user) {
      toast.info("Sign in to save your favourite cards.");
      login();
      return;
    }
    const deckId = deck === "tarot" ? "rider-waite" : "moon-nature";
    const isFav = favorites.has(card.id);
    setFavorites((prev) => {
      const next = new Set(prev);
      isFav ? next.delete(card.id) : next.add(card.id);
      return next;
    });
    try {
      if (isFav) await api.delete(`/favorites/${card.id}`);
      else await api.post("/favorites", { card_id: card.id, deck_id: deckId });
    } catch (err) {
      // Revert on failure.
      setFavorites((prev) => {
        const next = new Set(prev);
        isFav ? next.add(card.id) : next.delete(card.id);
        return next;
      });
      toast.error("Could not update your favourites.");
    }
  };

  const isTarot = deck === "tarot";
  const filters = isTarot ? TAROT_FILTERS : ORACLE_FILTERS;
  const source = isTarot ? tarot : oracle;

  const cards = useMemo(() => {
    const f = filters.find((x) => x.id === filter);
    const q = query.trim().toLowerCase();
    return source.filter((c) => {
      if (filter === "fav") {
        if (!favorites.has(c.id)) return false;
      } else if (f && !f.match(c)) {
        return false;
      }
      if (!q) return true;
      const name = (c.title || c.name || "").toLowerCase();
      const kw = (isTarot ? c.keywords_upright : c.keywords) || [];
      return name.includes(q) || kw.some((k) => k.toLowerCase().includes(q));
    });
  }, [source, filters, filter, query, isTarot, favorites]);

  const switchDeck = (d) => {
    setDeck(d);
    setFilter("all");
    setQuery("");
  };

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-10 sm:px-8" data-testid="library-page">
      {/* Header */}
      <div className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#D9A441]/30 px-4 py-1.5 text-[11px] uppercase tracking-[0.25em] text-[#E5C365]">
          <LibraryIcon className="h-3.5 w-3.5" /> The Card Library
        </span>
        <h1 className="mt-5 font-display text-4xl font-semibold text-[#F8F3E6] sm:text-5xl">
          Every card, laid bare.
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-[#A3A5B8] sm:text-base">
          Wander the full deck at your own pace. Tap any card to sit with its art, keywords, and
          meaning.
        </p>
      </div>

      {/* Deck toggle */}
      <div className="mt-9 flex justify-center">
        <div className="inline-flex rounded-full border border-[#D9A441]/25 bg-[#131422]/70 p-1" data-testid="library-deck-toggle">
          <button
            onClick={() => switchDeck("tarot")}
            data-testid="library-deck-tarot"
            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
              isTarot ? "bg-[#D9A441]/20 text-[#E5C365]" : "text-[#A3A5B8] hover:text-[#F8F3E6]"
            }`}
          >
            Whispering Path Tarot <span className="ml-1 text-[#8A8CA0]">78</span>
          </button>
          <button
            onClick={() => switchDeck("oracle")}
            data-testid="library-deck-oracle"
            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
              !isTarot ? "bg-[#D9A441]/20 text-[#E5C365]" : "text-[#A3A5B8] hover:text-[#F8F3E6]"
            }`}
          >
            Whispering Path Oracle <span className="ml-1 text-[#8A8CA0]">44</span>
          </button>
        </div>
      </div>

      {/* Search + filters */}
      <div className="mt-7 flex flex-col items-center gap-4">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A8CA0]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or keyword…"
            data-testid="library-search"
            className="h-11 rounded-full border-[#D9A441]/25 bg-[#131422]/70 pl-10 pr-10 text-[#F8F3E6] placeholder:text-[#8A8CA0] focus-visible:ring-[#D9A441]/40"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8CA0] hover:text-[#F8F3E6]"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-2" data-testid="library-filters">
          {user && (
            <button
              onClick={() => setFilter("fav")}
              data-testid="library-filter-fav"
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium tracking-wide transition-colors ${
                filter === "fav"
                  ? "border-[#C98089]/70 bg-[#722F37]/25 text-[#E8B4BC]"
                  : "border-[#C98089]/25 text-[#A3A5B8] hover:border-[#C98089]/50 hover:text-[#F8F3E6]"
              }`}
            >
              <Heart className={`h-3.5 w-3.5 ${filter === "fav" ? "fill-[#C98089] text-[#C98089]" : ""}`} />
              Favourites {favorites.size > 0 && <span className="text-[#8A8CA0]">{favorites.size}</span>}
            </button>
          )}
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`library-filter-${f.id}`}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium tracking-wide transition-colors ${
                filter === f.id
                  ? "border-[#D9A441]/60 bg-[#D9A441]/15 text-[#E5C365]"
                  : "border-[#D9A441]/15 text-[#A3A5B8] hover:border-[#D9A441]/35 hover:text-[#F8F3E6]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Count */}
      <p className="mt-6 text-center text-xs uppercase tracking-[0.2em] text-[#8A8CA0]" data-testid="library-count">
        {loading ? "Consulting the deck…" : `${cards.length} ${cards.length === 1 ? "card" : "cards"}`}
      </p>

      {/* Grid */}
      <div
        className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
        data-testid="library-grid"
      >
        {cards.map((card, i) => {
          const kw = (isTarot ? card.keywords_upright : card.keywords) || [];
          const tag = isTarot ? card.arcana === "Major" ? "Major" : card.suit : card.element;
          const tint = !isTarot ? ELEMENT_TINT[card.element] : "#D9A441";
          return (
            <motion.div
              key={card.id}
              role="button"
              tabIndex={0}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.4), duration: 0.35 }}
              onClick={() => setSelected(card)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelected(card);
                }
              }}
              data-testid={`library-card-${card.id}`}
              className="group relative aspect-[2/3] cursor-pointer overflow-hidden rounded-xl border border-[#D9A441]/20 bg-[#131422] text-left outline-none transition-all hover:-translate-y-1.5 hover:border-[#D9A441]/50 hover:shadow-[0_0_22px_rgba(217,164,65,0.28)] focus-visible:ring-2 focus-visible:ring-[#D9A441]"
            >
              <LibraryCardImage card={card} className="transition-transform duration-500 group-hover:scale-[1.05]" />
              <button
                onClick={(e) => toggleFavorite(card, e)}
                data-testid={`library-fav-${card.id}`}
                aria-label={favorites.has(card.id) ? "Remove favourite" : "Add favourite"}
                className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-[#D9A441]/30 bg-[#0B0C15]/70 backdrop-blur-sm transition-all hover:border-[#C98089]/70 hover:bg-[#722F37]/40"
              >
                <Heart
                  className={`h-4 w-4 transition-colors ${
                    favorites.has(card.id) ? "fill-[#C98089] text-[#C98089]" : "text-[#E5C365]"
                  }`}
                />
              </button>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0C15] via-[#0B0C15]/75 to-transparent px-3 pb-3 pt-10">
                {tag && (
                  <span
                    className="mb-1 inline-block rounded-full border px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]"
                    style={{ borderColor: `${tint}55`, color: tint }}
                  >
                    {tag}
                  </span>
                )}
                <h3 className="font-display text-sm font-semibold leading-tight text-[#F8F3E6]">
                  {card.title || card.name}
                </h3>
                <p className="mt-0.5 truncate text-[9px] uppercase tracking-[0.12em] text-[#A3A5B8]">
                  {kw.slice(0, 3).join(" · ")}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {!loading && cards.length === 0 && (
        <div className="mt-16 text-center text-[#A3A5B8]" data-testid="library-empty">
          <Sparkles className="mx-auto h-6 w-6 text-[#D9A441]/60" />
          <p className="mt-3 text-sm">No cards match your search. Try another word.</p>
        </div>
      )}

      {/* Detail dialog */}
      <CardDetailDialog
        card={selected}
        isTarot={isTarot}
        isFav={selected ? favorites.has(selected.id) : false}
        onToggleFav={() => selected && toggleFavorite(selected)}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

const CardDetailDialog = ({ card, isTarot, isFav, onToggleFav, onClose }) => {
  const open = Boolean(card);
  const tint = card && !isTarot ? ELEMENT_TINT[card.element] : "#D9A441";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl overflow-hidden border-[#D9A441]/25 bg-[#0F1019] p-0 text-[#F8F3E6]"
        data-testid="library-detail"
      >
        {card && (
          <div className="grid max-h-[85vh] grid-cols-1 overflow-y-auto sm:grid-cols-[minmax(0,42%)_1fr]">
            {/* Art */}
            <div className="relative aspect-[2/3] w-full border-b border-[#D9A441]/15 sm:border-b-0 sm:border-r">
              <LibraryCardImage card={card} />
            </div>

            {/* Meaning */}
            <div className="flex flex-col gap-4 p-6 sm:p-7">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  {(isTarot ? (card.arcana === "Major" ? ["Major Arcana"] : [card.suit]) : [card.element]).map(
                    (t) =>
                      t && (
                        <span
                          key={t}
                          className="rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em]"
                          style={{ borderColor: `${tint}55`, color: tint }}
                        >
                          {t}
                        </span>
                      )
                  )}
                  {isTarot && card.element && (
                    <span className="rounded-full border border-[#D9A441]/25 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] text-[#A3A5B8]">
                      {card.element}
                    </span>
                  )}
                </div>
                <DialogTitle asChild>
                  <h2 className="mt-3 font-display text-3xl font-semibold" data-testid="library-detail-title">
                    {card.title || card.name}
                  </h2>
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Meaning, keywords and reflection prompt for {card.title || card.name}.
                </DialogDescription>
                <button
                  onClick={onToggleFav}
                  data-testid="library-detail-fav"
                  className={`mt-3 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                    isFav
                      ? "border-[#C98089]/70 bg-[#722F37]/25 text-[#E8B4BC]"
                      : "border-[#D9A441]/30 text-[#E5C365] hover:border-[#C98089]/60 hover:bg-[#722F37]/20"
                  }`}
                >
                  <Heart className={`h-4 w-4 ${isFav ? "fill-[#C98089] text-[#C98089]" : ""}`} />
                  {isFav ? "Favourited" : "Add to favourites"}
                </button>
              </div>

              {isTarot ? (
                <>
                  <MeaningBlock label="Upright" tint="#E5C365" keywords={card.keywords_upright} text={card.upright} />
                  <MeaningBlock label="Reversed" tint="#C98089" keywords={card.keywords_reversed} text={card.reversed} reversed />
                </>
              ) : (
                <MeaningBlock label="Meaning" tint="#E5C365" keywords={card.keywords} text={card.extended_meaning || card.extended_message} />
              )}

              {card.journal_prompt && (
                <div className="mt-1 rounded-xl border border-[#D9A441]/20 bg-[#131422]/70 p-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#D9A441]">Reflect</p>
                  <p className="mt-1.5 font-display text-base italic leading-relaxed text-[#F8F3E6]">
                    {card.journal_prompt}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MeaningBlock = ({ label, tint, keywords, text, reversed }) => (
  <div>
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: tint }}>
        {label}
      </span>
      <span className="h-px flex-1" style={{ background: `${tint}33` }} />
    </div>
    {keywords && keywords.length > 0 && (
      <p className="mt-2 text-[10px] uppercase tracking-[0.14em] text-[#A3A5B8]">
        {keywords.slice(0, 4).join(" · ")}
      </p>
    )}
    {text && <p className="mt-2 text-sm leading-relaxed text-[#D7D3C6]">{text}</p>}
  </div>
);
